import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { useLanguage } from "../../../i18n/useLanguage";
import { useGetVehicleModels } from "../../../queries/useGetVehicleModels";
import { useSearchForm } from "../../../stores/hooks/useSearchForm";
import { ComboBox } from "../../../ui/ComboBox/ComboBox";
import { Input } from "../../../ui/Input/Input";
import { Spinner } from "../../../ui/Spinner";
import { CarIcon } from "../../../ui/icons/CarIcon";
import { ChevronRightIcon } from "../../../ui/icons/ChevronRightIcon";
import { usePlacement } from "./usePlacement";

type Props = {
  extraOptions?: NonNullable<ReturnType<typeof useGetVehicleModels>["data"]>;
  onSelectModelId?(vehicleModelId: string): void;
};

export const ModelInput: FunctionComponent<Props> = ({
  extraOptions = [],
  onSelectModelId,
}) => {
  const { t } = useTranslation();
  const { data, isLoading, selectedOptionLabel } = useGetVehicleModels();
  const [language] = useLanguage();
  const [{ vehicleManufacturerName, vehicleModelName }, { setModel }] =
    useSearchForm();
  const options = [...(data || []), ...extraOptions].filter(
    (option, index, allOptions) =>
      allOptions.findIndex(({ value }) => value === option.value) === index
  );
  const selectedLabel =
    selectedOptionLabel ||
    options.find(
      ({ label }) => label.toLowerCase() === vehicleModelName?.toLowerCase()
    )?.label ||
    "";

  const open = !vehicleModelName && options.length > 0;
  const disabled = !vehicleManufacturerName;

  return (
    <ComboBox
      onSelectOption={(option): void => {
        const nextVehicleModelName =
          option?.data.vehicleModelName[language === "fr" ? "fr" : "en"] ||
          null;
        const nextVehicleModelClassId = Number(option?.data.vehicleModelClassId)
          ? option!.data.vehicleModelClassId
          : null;
        setModel(nextVehicleModelName, nextVehicleModelClassId);
        if (option?.value && onSelectModelId) {
          onSelectModelId(String(option.value));
        }
      }}
      id="model-combo"
      open={open}
      options={options}
      placement={usePlacement()}
    >
      <Input
        after={isLoading ? <Spinner /> : <ChevronRightIcon />}
        aria-label="selectedModel"
        before={<CarIcon />}
        disabled={disabled}
        name="model"
        placeholder={t("searchForm.selectModel")}
        readOnly={!options.length}
        ring={!selectedLabel && !disabled}
        value={selectedLabel}
      />
    </ComboBox>
  );
};
